"use client";
import { Link } from "@/lib/i18n/navigation";
import { useTranslations } from "next-intl";
import type { FiltersValue } from "./SearchFilters";

type Chip = { label: string; href: string };

function buildHref(
  baseParams: Record<string, string>,
  filters: FiltersValue,
  removeKey: string,
  removeValue?: string,
): string {
  const next: FiltersValue = { ...filters };
  switch (removeKey) {
    case "availability":
      next.availability = undefined;
      break;
    case "gender":
      next.gender = undefined;
      break;
    case "video":
    case "homeVisit":
    case "urgentCare":
    case "wheelchairAccess":
    case "newPatients":
      (next as any)[removeKey] = false;
      break;
    case "priceMax":
      next.priceMax = undefined;
      break;
    case "minRating":
      next.minRating = undefined;
      break;
    case "convention":
      next.conventions = next.conventions.filter((c) => c !== removeValue);
      break;
    case "language":
      next.languages = next.languages.filter((l) => l !== removeValue);
      break;
  }

  const params: Record<string, string> = { ...baseParams };
  if (next.availability) params.availability = next.availability;
  if (next.gender) params.gender = next.gender;
  if (next.conventions.length) params.conventions = next.conventions.join(",");
  if (next.languages.length) params.languages = next.languages.join(",");
  if (next.video) params.video = "1";
  if (next.homeVisit) params.homeVisit = "1";
  if (next.urgentCare) params.urgentCare = "1";
  if (next.wheelchairAccess) params.wheelchairAccess = "1";
  if (next.newPatients) params.newPatients = "1";
  if (next.priceMax) params.priceMax = String(next.priceMax);
  if (next.minRating) params.minRating = String(next.minRating);

  const qs = new URLSearchParams(params).toString();
  return `/recherche${qs ? `?${qs}` : ""}`;
}

export function ActiveFiltersChips({
  filters,
  baseParams,
}: {
  filters: FiltersValue;
  baseParams: Record<string, string>;
}) {
  const t = useTranslations("search");
  const SERVICE_LABELS: Record<string, string> = {
    video: t("filtersChips.video"),
    homeVisit: t("filtersChips.homeVisit"),
    urgentCare: t("filtersChips.urgentCare"),
    wheelchairAccess: t("filtersChips.wheelchairAccess"),
    newPatients: t("filtersChips.newPatients"),
  };
  const LANG_LABELS = (t.raw("languages") as Record<string, string>);
  const AVAIL_LABELS: Record<string, string> = {
    today: t("filtersChips.todayLabel"),
    tomorrow: t("filtersChips.tomorrowLabel"),
    thisWeek: t("filtersChips.thisWeekLabel"),
  };

  const chips: Chip[] = [];

  if (filters.availability) {
    chips.push({
      label: AVAIL_LABELS[filters.availability]!,
      href: buildHref(baseParams, filters, "availability"),
    });
  }
  if (filters.gender) {
    chips.push({
      label: filters.gender === "female" ? t("filtersChips.female") : t("filtersChips.male"),
      href: buildHref(baseParams, filters, "gender"),
    });
  }
  for (const c of filters.conventions) {
    chips.push({ label: c, href: buildHref(baseParams, filters, "convention", c) });
  }
  for (const l of filters.languages) {
    chips.push({
      label: LANG_LABELS[l] ?? l,
      href: buildHref(baseParams, filters, "language", l),
    });
  }
  for (const k of [
    "video",
    "homeVisit",
    "urgentCare",
    "wheelchairAccess",
    "newPatients",
  ] as const) {
    if (filters[k]) {
      chips.push({ label: SERVICE_LABELS[k]!, href: buildHref(baseParams, filters, k) });
    }
  }
  if (filters.priceMax) {
    chips.push({
      label: t("filtersChips.priceMax", { value: filters.priceMax }),
      href: buildHref(baseParams, filters, "priceMax"),
    });
  }
  if (filters.minRating) {
    chips.push({
      label: t("filtersChips.ratingChip", { value: filters.minRating }),
      href: buildHref(baseParams, filters, "minRating"),
    });
  }

  if (chips.length === 0) return null;

  const clearHref = `/recherche${
    Object.keys(baseParams).length
      ? `?${new URLSearchParams(baseParams).toString()}`
      : ""
  }`;

  return (
    <div className="-mx-4 flex items-center gap-2 overflow-x-auto scroll-smooth px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:-mx-6 sm:px-6 lg:mx-0 lg:flex-wrap lg:overflow-visible lg:px-0 lg:pb-0">
      <span className="shrink-0 text-[11px] font-semibold uppercase tracking-widest text-[var(--sahha-text-500)]">
        {t("filtersChips.activeHeading")}
      </span>
      {chips.map((chip, i) => (
        <Link
          key={i}
          href={chip.href as never}
          className="group inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full bg-[var(--sahha-primary-600)] px-3 text-[12.5px] font-medium text-white shadow-[0_2px_6px_-2px_rgba(21,112,239,0.4)] hover:bg-[var(--sahha-primary-700)]"
          aria-label={t("filtersChips.removeAria", { label: chip.label })}
        >
          <span className="whitespace-nowrap">{chip.label}</span>
          <span className="flex h-4 w-4 items-center justify-center rounded-full bg-white/20 transition-colors group-hover:bg-white/30">
            <svg viewBox="0 0 16 16" className="h-2.5 w-2.5" aria-hidden>
              <path d="M3 3l10 10M13 3L3 13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          </span>
        </Link>
      ))}
      {chips.length > 1 && (
        <Link
          href={clearHref as never}
          className="shrink-0 inline-flex h-8 items-center gap-1 rounded-full border border-[var(--sahha-border-default)] bg-white px-3 text-[12.5px] font-medium text-[var(--sahha-text-700)] hover:border-[var(--sahha-danger)] hover:bg-red-50 hover:text-[var(--sahha-danger)]"
        >
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" aria-hidden>
            <path d="M3 12a9 9 0 1 0 3-6.7L3 8M3 3v5h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {t("filtersChips.removeAll")}
        </Link>
      )}
    </div>
  );
}
